import React, {
  createContext,
  type FC,
  useEffect,
  type Dispatch,
  type SetStateAction,
  useState,
} from 'react';
import type { AppTemplate } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import type { WorkspaceType } from '@wix/ambassador-os-v1-workspace/types';
import { getIsLoggedInByCookie } from '../utils/getIsLoggedInByCookie';
import { useEssentials } from '@wix/fe-essentials-standalone';
import { useTemplatesQuery } from '../hooks/useTemplatesQuery';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { useTemplatesPageBi } from '../hooks/useTemplatesPageBi';
import { ActiveModal } from '../types/ActiveModal';

export interface TemplatesContextProps {
  templates: AppTemplate[];
  isUserLoggedIn: boolean;
  templatesLoaded: boolean;
  workspace: WorkspaceType | undefined;
  activeModal: ActiveModal;
  setActiveModal: Dispatch<SetStateAction<ActiveModal>>;
  currentTemplate: AppTemplate;
  setCurrentTemplate: Dispatch<SetStateAction<AppTemplate>>;
}

export const TemplatesContext = createContext<TemplatesContextProps>({
  templates: [],
  isUserLoggedIn: false,
  templatesLoaded: false,
  workspace: undefined,
  activeModal: ActiveModal.NONE,
  setActiveModal: () => {},
  currentTemplate: undefined,
  setCurrentTemplate: () => {},
});

export const TemplatesContextProvider: FC = ({ children }) => {
  const isUserLoggedIn = getIsLoggedInByCookie();
  const { panoramaClient } = useEssentials();
  const { reportMainPageLoaded } = useTemplatesPageBi();
  const [activeModal, setActiveModal] = React.useState<ActiveModal>(
    ActiveModal.NONE,
  );
  const [currentTemplate, setCurrentTemplate] = useState<AppTemplate | null>(
    null,
  );

  const {
    data: templates = [],
    isLoading: templatesLoading,
    error: templatesError,
  } = useTemplatesQuery();

  const {
    data: workspace,
    isLoading: workspaceLoading,
    error: workspaceError,
  } = useWorkspaceQuery();
  const isDataReady = !templatesLoading && !workspaceLoading;

  useEffect(() => {
    panoramaClient.reportLoadStart();
  }, [panoramaClient]);

  useEffect(() => {
    if (isDataReady) {
      panoramaClient.reportLoadFinish();
      reportMainPageLoaded();
    }
    if (templatesError) {
      panoramaClient.errorMonitor().reportError(templatesError);
    }
    if (workspaceError) {
      panoramaClient.errorMonitor().reportError(workspaceError);
    }
  }, [
    panoramaClient,
    isDataReady,
    reportMainPageLoaded,
    templatesError,
    workspaceError,
  ]);

  return (
    <TemplatesContext.Provider
      value={{
        templates,
        isUserLoggedIn,
        templatesLoaded: isDataReady,
        workspace: workspace?.type,
        activeModal,
        setActiveModal,
        currentTemplate,
        setCurrentTemplate,
      }}
    >
      {children}
    </TemplatesContext.Provider>
  );
};
